import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  CheckSquare,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Save,
  Settings,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TasksPage = () => {
  const { t } = useTranslation('workflows');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [closureModeFilter, setClosureModeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instruction: '',
    closure_mode: 'system',
    order: 0,
    status: 'active'
  });

  useEffect(() => {
    fetchTasks();
  }, [page, statusFilter, closureModeFilter]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (closureModeFilter !== 'all') params.append('closure_mode', closureModeFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/workflows/tasks?${params}`);
      setTasks(response.data.data);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error(t('tasks.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchTasks();
  };

  const openForm = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        name: task.name,
        description: task.description || '',
        instruction: task.instruction,
        closure_mode: task.closure_mode,
        order: task.order,
        status: task.status
      });
    } else {
      setEditingTask(null);
      setFormData({
        name: '',
        description: '',
        instruction: '',
        closure_mode: 'system',
        order: 0,
        status: 'active'
      });
    }
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.instruction) {
      toast.error(t('tasks.messages.nameInstructionRequired'));
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingTask) {
        await axios.put(`${API_URL}/api/workflows/tasks/${editingTask.id}`, formData);
        toast.success(t('tasks.messages.updated'));
      } else {
        await axios.post(`${API_URL}/api/workflows/tasks`, formData);
        toast.success(t('tasks.messages.created'));
      }
      setShowForm(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tasks.messages.saveError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm(t('tasks.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/workflows/tasks/${taskId}`);
      toast.success(t('tasks.messages.deleted'));
      fetchTasks();
    } catch (error) {
      toast.error(t('tasks.messages.deleteError'));
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('tasks.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('tasks.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('tasks.searchPlaceholder')}
              className="pl-10"
              data-testid="task-search"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder={t('tasks.status.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('tasks.status.all')}</SelectItem>
            <SelectItem value="active">{t('tasks.status.active')}</SelectItem>
            <SelectItem value="inactive">{t('tasks.status.inactive')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={closureModeFilter} onValueChange={(v) => { setClosureModeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="closure-filter">
            <SelectValue placeholder={t('tasks.closureMode.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('tasks.closureMode.all')}</SelectItem>
            <SelectItem value="system">{t('tasks.closureMode.system')}</SelectItem>
            <SelectItem value="agent">{t('tasks.closureMode.agent')}</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => openForm()} className="bg-[#E63946] hover:bg-[#d32836]" data-testid="new-task-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('tasks.newTask')}
        </Button>
      </div>

      {/* Tasks Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : tasks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tasks.map(task => (
            <Card key={task.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-[var(--red-soft,#FBE6E8)] flex items-center justify-center">
                      <CheckSquare className="h-6 w-6 text-[var(--red,#E80114)]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[16px] text-[var(--ink,#1A1414)]">{task.name}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant={task.status === 'active' ? 'default' : 'secondary'} className="text-[12px] px-3 py-1">
                          {task.status === 'active' ? t('tasks.status.active') : t('tasks.status.inactive')}
                        </Badge>
                        <Badge variant="outline" className="gap-1.5 text-[12px] px-3 py-1 bg-[var(--bg-soft,#F7F4F1)]">
                          {task.closure_mode === 'system' ? (
                            <>
                              <Settings className="h-3.5 w-3.5" />
                              {t('agentQueues.detail.auto')}
                            </>
                          ) : (
                            <>
                              <User className="h-3.5 w-3.5" />
                              {t('agentQueues.detail.manual')}
                            </>
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                {task.description && (
                  <p className="text-[14px] text-[var(--ink-2,#4A3F3F)] mb-4 leading-relaxed">{task.description}</p>
                )}
                
                <div className="bg-[var(--bg-soft,#F7F4F1)] rounded-xl p-4 mb-4">
                  <p className="text-[12px] text-[var(--ink-3,#8B7E7E)] font-semibold mb-2 uppercase tracking-wide">{t('tasks.instruction')}:</p>
                  <p className="text-[14px] text-[var(--ink,#1A1414)] line-clamp-3 leading-relaxed">{task.instruction}</p>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-[var(--line-soft,#F4F0EC)]">
                  <span className="text-[13px] text-[var(--ink-3,#8B7E7E)] font-medium">{t('tasks.order')}: {task.order}</span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openForm(task)} data-testid={`edit-task-${task.id}`} className="h-9 w-9 p-0">
                      <Edit className="h-5 w-5 text-[var(--ink-3)]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(task.id)} data-testid={`delete-task-${task.id}`} className="h-9 w-9 p-0">
                      <Trash2 className="h-5 w-5 text-[var(--red,#E80114)]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <CheckSquare className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p>{t('tasks.noTasks')}</p>
          <p className="text-sm mt-2">{t('tasks.noTasksSubtitle')}</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-4 text-sm">
            {t('tasks.page', { current: page, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Task Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTask ? t('tasks.form.editTitle') : t('tasks.form.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('tasks.form.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('tasks.form.namePlaceholder')}
                  data-testid="task-name"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('tasks.form.executionOrder')}</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  data-testid="task-order"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t('tasks.form.description')}</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('tasks.form.descriptionPlaceholder')}
                data-testid="task-description"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('tasks.form.instruction')} *</Label>
              <Textarea
                value={formData.instruction}
                onChange={(e) => setFormData({...formData, instruction: e.target.value})}
                placeholder={t('tasks.form.instructionPlaceholder')}
                rows={6}
                data-testid="task-instruction"
              />
              <p className="text-xs text-gray-500">
                {t('tasks.form.instructionVariables')}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('tasks.form.closureMode')}</Label>
                <Select
                  value={formData.closure_mode}
                  onValueChange={(v) => setFormData({...formData, closure_mode: v})}
                >
                  <SelectTrigger data-testid="task-closure-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">
                      <span className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        {t('tasks.closureMode.system')}
                      </span>
                    </SelectItem>
                    <SelectItem value="agent">
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {t('tasks.closureMode.agent')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formData.closure_mode === 'system' 
                    ? t('tasks.closureMode.systemDescription')
                    : t('tasks.closureMode.agentDescription')}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{t('tasks.form.status')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger data-testid="task-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('tasks.status.active')}</SelectItem>
                    <SelectItem value="inactive">{t('tasks.status.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={formLoading}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={formLoading} data-testid="save-task-btn">
              {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingTask ? t('common.update') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
